import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Phone, Mail, MapPin, UsersRound,
  Heart, Sparkles, Handshake, Compass,
} from "lucide-react";

/* ----- Images (curated warm lifestyle photography) ----- */
const HERO_IMG = "/generated/hero-wecare.jpg";
const SERVICE_INHOME_IMG = "/generated/service-inhome.jpg";
const SERVICE_PCA_IMG    = "/generated/pca-overnight.jpg";
const SERVICE_RSL_IMG    = "/generated/service-residential.jpg";

/* Abstract mark inspired by the WeCare II figure — subtle brand accent */
function BrandFigure({ className = "", color = "currentColor", size = 60, opacity = 1 }) {
  return (
    <svg viewBox="0 0 60 90" width={size} height={size * 1.5} className={className} fill="none" style={{ opacity }} aria-hidden="true">
      <circle cx="30" cy="12" r="8" fill={color} />
      <path d="M30 24 C 44 24, 50 36, 46 54 C 44 66, 38 76, 30 84 C 22 76, 16 66, 14 54 C 10 36, 16 24, 30 24 Z" fill={color} />
    </svg>
  );
}

function Eyebrow({ children, tone = "navy", "data-testid": testId }) {
  const color = tone === "orange" ? "text-brand-orange" : tone === "white" ? "text-orange-200" : "text-brand-navy";
  return (
    <div className={`inline-flex items-center gap-2.5 text-[10px] md:text-[11px] font-bold uppercase tracking-[0.28em] ${color}`} data-testid={testId}>
      <span className="h-px w-8 bg-brand-orange" />
      {children}
    </div>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* =============== 1. HERO =============== */}
      <section className="relative bg-brand-cream pt-4 md:pt-6 pb-20 md:pb-28" data-testid="hero-section">
        <BrandFigure className="absolute top-[10%] left-[3%] hidden lg:block" color="#1e2a78" size={22} opacity={0.05} />

        <div className="wc-container relative">
          <div className="mb-6 md:mb-8">
            <Eyebrow data-testid="hero-eyebrow">Supportive In-Home Services · Virginia</Eyebrow>
          </div>

          <div className="relative">
            <div className="relative lg:ml-[35%]">
              <div className="relative aspect-[4/5] sm:aspect-[16/10] lg:aspect-auto lg:h-[600px] xl:h-[640px] rounded-[2rem] overflow-hidden shadow-[0_30px_60px_rgba(30,42,120,0.12)]">
                <img
                  src={HERO_IMG}
                  alt="A White adult man with Down syndrome laughs joyfully while walking through a sunny farmers market alongside a Black female WeCare II Direct Support Professional in a navy wecare polo"
                  className="w-full h-full object-cover"
                  data-testid="hero-image"
                />
              </div>
            </div>

            <div
              className="relative lg:absolute lg:top-1/2 lg:left-0 lg:-translate-y-1/2 lg:w-[52%] xl:w-[48%]
                         -mt-16 lg:mt-0 mx-4 lg:mx-0 wc-fade-up"
              data-testid="hero-card"
            >
              <div className="absolute -left-3 top-8 hidden md:block w-3 h-24 rounded-l-md bg-brand-orange" aria-hidden="true" />

              <div className="relative bg-brand-navy text-white rounded-[1.75rem] p-8 md:p-12 lg:p-14 shadow-[0_30px_60px_rgba(15,26,74,0.30)]">
                <h1
                  className="font-outfit font-semibold tracking-tight leading-[1.05] text-white text-[2rem] sm:text-[2.5rem] lg:text-[2.75rem] xl:text-[3.25rem]"
                  data-testid="hero-headline"
                >
                  Together, we can create a{" "}
                  <span className="italic font-medium text-brand-orange" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
                    brighter
                  </span>{" "}
                  future for those{" "}
                  <span className="whitespace-nowrap">
                    we{" "}
                    <span className="italic font-medium text-brand-orange" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
                      care
                    </span>{" "}
                    for.
                  </span>
                </h1>
                <p className="mt-5 md:mt-6 text-white/80 text-[15px] md:text-base lg:text-[17px] leading-relaxed max-w-lg" data-testid="hero-copy">
                  WeCare II provides person-centered support that helps individuals with disabilities live safely,
                  confidently and as independently as possible — at home and in their communities.
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <Link to="/services" className="inline-flex items-center justify-center gap-2 bg-brand-orange text-white hover:brightness-95 rounded-full px-7 py-3.5 font-semibold text-sm shadow-sm" style={{ transition: "filter 200ms ease, transform 200ms ease" }} data-testid="hero-services-btn">
                    Explore Our Services <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/refer" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 text-white hover:bg-white/10 px-7 py-[calc(0.875rem-1px)] font-medium text-sm" style={{ transition: "background-color 200ms ease" }} data-testid="hero-refer-btn">
                    Refer an Individual
                  </Link>
                </div>
                <div className="mt-8 flex items-center gap-4 pt-6 border-t border-white/10 text-[11px] uppercase tracking-[0.22em] text-white/60 font-semibold" data-testid="hero-trust">
                  <span className="text-brand-orange">●</span> DBHDS-Licensed · Virginia
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =============== Info strip: referral + contact (combined, compact) =============== */}
      <section className="bg-brand-navy/[0.05] backdrop-blur-sm border-y border-brand-navy/15" data-testid="info-strip">
        <div className="wc-container py-4 md:py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-6">
          {/* LEFT — now accepting */}
          <div className="flex items-center gap-3" data-testid="info-strip-accepting">
            <span className="relative flex w-2 h-2 shrink-0">
              <span className="absolute inline-flex w-full h-full rounded-full bg-brand-orange opacity-75 animate-ping" />
              <span className="relative inline-flex w-2 h-2 rounded-full bg-brand-orange" />
            </span>
            <div className="leading-tight">
              <div className="text-[11px] md:text-xs uppercase tracking-[0.22em] font-bold text-brand-navy">
                Now Accepting Referrals
              </div>
              <div className="mt-0.5 text-[11px] md:text-[12px] text-brand-navy/70">
                Hampton Roads &amp; Eastern Shore
              </div>
            </div>
          </div>

          {/* CENTER — contact */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 md:justify-center" data-testid="info-strip-contact">
            <a href="tel:7579519555" className="inline-flex items-center gap-2 text-[14px] md:text-[15px] font-medium text-brand-navy hover:text-brand-orange" style={{ transition: "color 200ms ease" }} data-testid="info-strip-phone">
              <Phone className="w-4 h-4 text-brand-orange" strokeWidth={2} /> 757-951-9555
            </a>
            <span className="hidden md:inline-block w-px h-4 bg-brand-navy/20" aria-hidden="true" />
            <a href="mailto:info@wecareii.com" className="inline-flex items-center gap-2 text-[14px] md:text-[15px] font-medium text-brand-navy hover:text-brand-orange" style={{ transition: "color 200ms ease" }} data-testid="info-strip-email">
              <Mail className="w-4 h-4 text-brand-orange" strokeWidth={2} /> info@wecareii.com
            </a>
          </div>

          {/* RIGHT — CTA */}
          <div className="md:text-right">
            <Link to="/refer" className="inline-flex items-center gap-1.5 text-[11px] md:text-xs uppercase tracking-[0.22em] font-bold text-brand-orange hover:brightness-90" data-testid="info-strip-refer-cta">
              Start a Referral <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* =============== 3. THREE CORE SERVICES =============== */}
      <section className="pt-12 md:pt-16 pb-10 md:pb-12 bg-brand-cream" data-testid="services-section">
        <div className="wc-container">
          <div className="max-w-2xl">
            <Eyebrow>How We Care</Eyebrow>
            <h2 className="mt-4 font-outfit text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-brand-navy leading-[1.08]">
              Support designed around{" "}
              <span className="italic font-medium text-brand-orange relative whitespace-nowrap" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
                you
                <svg className="absolute -bottom-1.5 left-0 w-full" viewBox="0 0 220 12" preserveAspectRatio="none" aria-hidden="true">
                  <path d="M 4 8 C 60 2, 130 12, 216 4" stroke="#f39325" strokeWidth="3" fill="none" strokeLinecap="round" />
                </svg>
              </span>
              <span className="text-brand-navy">.</span>
            </h2>
            <p className="mt-5 text-slate-600 text-base md:text-lg leading-relaxed">
              Every individual is different. Our services are designed around each person&apos;s needs, goals, routines,
              preferences, and authorized service plan.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7 items-stretch">
            {[
              {
                num: "01",
                title: "Supportive In-Home Services",
                img: SERVICE_INHOME_IMG,
                alt: "A young adult who uses a wheelchair laughs joyfully in a residential neighborhood as a WeCare II caregiver in a navy wecare scrub top hands her a soft ball",
                body: "Individualized support in the home and community focused on daily routines, skill development, independence, community participation, and personal goals.",
                testId: "service-inhome",
              },
              {
                num: "02",
                title: "Personal Care Assistance",
                badge: "Overnight Support",
                img: SERVICE_PCA_IMG,
                alt: "A WeCare II Direct Support Professional in a navy polo gently adjusts the duvet for an adult client peacefully resting in a cozy home bedroom at nighttime",
                body: "Individualized overnight assistance that supports personal needs, nighttime routines, safety, comfort, and well-being.",
                testId: "service-pca",
              },
              {
                num: "03",
                title: "Residential Supported Living",
                img: SERVICE_RSL_IMG,
                alt: "An adult client stirs a pot on her home stovetop while a WeCare II caregiver in a navy wecare scrub top supportively guides her through the recipe",
                body: "Individualized residential support designed to help people develop and maintain the skills needed to live as safely and independently as possible.",
                testId: "service-residential",
              },
            ].map((s) => (
              <div key={s.title} className="group bg-brand-navy/[0.04] backdrop-blur-sm rounded-3xl overflow-hidden border border-brand-navy/15 flex flex-col hover:bg-brand-navy/[0.06] hover:border-brand-navy/25 hover:shadow-[0_28px_50px_rgba(30,42,120,0.12)]" style={{ transition: "background-color 300ms ease, border-color 300ms ease, box-shadow 300ms ease" }} data-testid={s.testId}>
                <div className="relative aspect-[16/11] overflow-hidden">
                  <img src={s.img} alt={s.alt} className="w-full h-full object-cover group-hover:scale-[1.03]" style={{ transition: "transform 500ms ease" }} />
                  <span className="absolute top-4 left-4 bg-brand-ivory/95 backdrop-blur rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.22em] font-bold text-brand-navy">{s.num}</span>
                </div>
                <div className="p-7 md:p-8 flex flex-col flex-1">
                  <h3 className="font-outfit text-xl md:text-[22px] font-semibold text-brand-navy tracking-tight leading-snug">{s.title}</h3>
                  {s.badge && (
                    <div className="mt-1 text-[11px] font-bold uppercase tracking-[0.22em] text-brand-orange">{s.badge}</div>
                  )}
                  <p className="mt-3 text-slate-600 leading-relaxed text-[15px]">{s.body}</p>
                  <Link to="/services" className="mt-6 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-brand-orange hover:brightness-90 self-start" data-testid={`${s.testId}-cta`}>
                    Explore Service <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =============== 4. THE WECARE DIFFERENCE — compact (no photo) =============== */}
      <section className="pt-10 md:pt-12 pb-16 md:pb-20 bg-brand-sand/50" data-testid="why-section">
        <div className="wc-container grid grid-cols-1 lg:grid-cols-[42fr_58fr] gap-10 lg:gap-16 items-start">
          <div>
            <Eyebrow>The WeCare Difference</Eyebrow>
            <h2 className="mt-4 font-outfit text-3xl md:text-4xl lg:text-[2.5rem] font-semibold tracking-tight text-brand-navy leading-[1.1]">
              Care Is More Than<br />Completing a{" "}
              <span className="italic font-medium text-brand-orange relative whitespace-nowrap" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
                Task
                <svg className="absolute -bottom-1.5 left-0 w-full" viewBox="0 0 220 12" preserveAspectRatio="none" aria-hidden="true">
                  <path d="M 4 8 C 60 2, 130 12, 216 4" stroke="#f39325" strokeWidth="3" fill="none" strokeLinecap="round" />
                </svg>
              </span>
              <span className="text-brand-navy">.</span>
            </h2>
            <p className="mt-5 text-slate-600 text-[15px] md:text-base leading-relaxed max-w-md">
              Meaningful support starts with understanding the individual. We provide respectful, dependable care
              centered around each person&apos;s goals, choices, and everyday life.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            {[
              { icon: Compass, title: "Person-Centered Support", body: "Built around individual goals, preferences, and routines." },
              { icon: Sparkles, title: "Independence & Daily Living", body: "Support that encourages confidence and everyday independence." },
              { icon: Heart, title: "Compassionate Professionals", body: "Respectful, dependable professionals who care about the work they do." },
              { icon: Handshake, title: "Family & Community Connection", body: "Collaboration with families, support teams, and community partners." },
            ].map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="bg-brand-bone/80 rounded-xl border border-brand-stone/70 p-5"
                data-testid={`why-${title.toLowerCase().replace(/[^a-z]+/g,'-')}`}
              >
                <div className="w-9 h-9 rounded-lg bg-brand-bone border border-brand-stone/70 grid place-items-center text-brand-orange">
                  <Icon className="w-4 h-4" strokeWidth={1.7} />
                </div>
                <div className="mt-3 font-outfit text-[15px] font-semibold text-brand-navy leading-snug">{title}</div>
                <p className="mt-1.5 text-[13.5px] text-slate-600 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =============== 5. SERVICE AREA — compact =============== */}
      <section className="py-14 md:py-16 bg-brand-ivory border-y border-brand-stone/60" data-testid="service-area-section">
        <div className="wc-container grid grid-cols-1 lg:grid-cols-[46fr_54fr] gap-8 lg:gap-14 items-center">
          <div>
            <div className="inline-flex items-center gap-2.5 text-[10px] md:text-[11px] font-bold uppercase tracking-[0.28em] text-brand-navy">
              <MapPin className="w-3.5 h-3.5 text-brand-orange" strokeWidth={2} />
              Service Area
            </div>
            <h2 className="mt-3 font-outfit text-2xl md:text-3xl lg:text-[2.25rem] font-semibold tracking-tight text-brand-navy leading-[1.15]">
              Proudly serving{" "}
              <span className="italic font-medium text-brand-orange whitespace-nowrap" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
                Hampton Roads
              </span>{" "}
              &amp; the Eastern Shore.
            </h2>
            <p className="mt-3 text-slate-600 text-[15px] leading-relaxed max-w-lg">
              WeCare II provides services across communities throughout Hampton Roads and Virginia&apos;s Eastern Shore.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 lg:gap-10 lg:justify-items-end" data-testid="service-area-regions">
            <div data-testid="service-area-hampton-roads">
              <div className="flex items-center gap-2.5 text-[10px] md:text-[11px] font-bold uppercase tracking-[0.28em] text-brand-navy">
                <span className="h-px w-6 bg-brand-orange" />
                Hampton Roads
              </div>
              <ul className="mt-4 space-y-2.5 text-[15px] text-brand-navy font-medium">
                <li>Virginia Beach · Chesapeake · Norfolk · Portsmouth</li>
                <li>Suffolk · Hampton · Newport News</li>
              </ul>
            </div>

            <div data-testid="service-area-eastern-shore">
              <div className="flex items-center gap-2.5 text-[10px] md:text-[11px] font-bold uppercase tracking-[0.28em] text-brand-navy">
                <span className="h-px w-6 bg-brand-orange" />
                Eastern Shore
              </div>
              <p className="mt-4 text-[15px] text-brand-navy font-medium leading-relaxed">
                Virginia&apos;s Eastern Shore communities
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* =============== 7. FINAL CTA =============== */}
      <section className="py-20 md:py-24 bg-brand-navy text-white relative overflow-hidden" data-testid="final-cta">
        <svg className="absolute -bottom-24 -left-24 w-[400px] h-[400px] pointer-events-none opacity-60" viewBox="0 0 400 400" aria-hidden="true">
          <defs>
            <linearGradient id="cta-orange" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#f39325" stopOpacity="0.20" />
              <stop offset="100%" stopColor="#f39325" stopOpacity="0" />
            </linearGradient>
          </defs>
          <circle cx="200" cy="200" r="200" fill="url(#cta-orange)" />
        </svg>

        <div className="wc-container relative text-center max-w-3xl">
          <div className="inline-flex items-center gap-2.5 text-[10px] md:text-[11px] font-bold uppercase tracking-[0.28em] text-brand-orange">
            <span className="h-px w-8 bg-brand-orange" />
            We&apos;re Here to Help
            <span className="h-px w-8 bg-brand-orange" />
          </div>
          <h2 className="mt-5 font-outfit text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.08]">
            Ready to Get Started With WeCare II?
          </h2>
          <p className="mt-5 text-white/80 text-base md:text-lg leading-relaxed mx-auto max-w-2xl">
            Whether you&apos;re looking for services, making a referral, or simply have questions, our team is
            ready to help you determine the next step.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="wc-btn-orange !uppercase !tracking-widest text-xs font-bold" data-testid="final-request-btn">
              Request Services
            </Link>
            <Link to="/refer" className="inline-flex items-center gap-2 rounded-full border-2 border-white/40 hover:bg-white/10 px-7 py-3 text-xs font-bold uppercase tracking-widest text-white" style={{ transition: "background-color 200ms ease" }} data-testid="final-refer-btn">
              Make a Referral <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-white/70">
            <a href="tel:7579519555" className="inline-flex items-center gap-2 hover:text-white"><Phone className="w-4 h-4 text-brand-orange"/> 757-951-9555</a>
            <a href="mailto:info@wecareii.com" className="inline-flex items-center gap-2 hover:text-white"><Mail className="w-4 h-4 text-brand-orange"/> info@wecareii.com</a>
          </div>
        </div>
      </section>
    </div>
  );
}
